import React, { useEffect, useState } from 'react';
import Icon from '../../shared/Icon';
import { UseProposalWorkspaceResult } from '../../../hooks/useProposalWorkspace';
import { WorkspaceActions } from '../ProposalWorkspace';
import { SURFACE_CENTRAL, TONE_BADGE } from '../../../utils/statusColors';
import { getStatusInfo } from '../procurementUi';
import { fmtDateTime } from '../../../utils/formatters';
import { procurementService } from '../../../services/api';
import DesfechoPicker from '../DesfechoPicker';

/**
 * Estágios DISPUTA e RESULTADO.
 *
 * Disputa: onde está a sessão e o caminho pra adequar a proposta ao lance
 * final (a corrida de 2h que motivou toda a fase 1 deste trabalho).
 * Resultado: o que o portal decidiu — homologação e concorrentes, que o
 * sistema já guardava (procurement_competitors) e a tela nunca mostrou.
 */

interface Props {
  ws: UseProposalWorkspaceResult;
  actions: WorkspaceActions;
  fmt: (v: any) => string;
  modo: 'disputa' | 'resultado';
  /** CTA "Marcar resultado" do cabeçalho pediu para abrir o seletor. */
  abrirDesfecho?: boolean;
  onDesfechoFechado?: () => void;
}

export default function DisputaResultadoSection({ ws, actions, fmt, modo, abrirDesfecho, onDesfechoFechado }: Props) {
  const { proposal, item } = ws;
  const [competidores, setCompetidores] = useState<any[] | null>(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!item?.id) return;
    setCarregando(true);
    procurementService.getCompetitors(item.id)
      .then((r: any) => { if (alive) setCompetidores(Array.isArray(r) ? r : r?.data || []); })
      .catch(() => { if (alive) setCompetidores([]); })
      .finally(() => { if (alive) setCarregando(false); });
    return () => { alive = false; };
  }, [item?.id]);

  const temItensTipados = ws.items.some((it: any) => it.chargeType);
  const homologado = Number(item?.valorHomologado) || 0;

  return (
    <div className="space-y-4">
      {modo === 'disputa' && (
        <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
          <div className="flex items-center gap-2 text-sm text-slate-200 font-medium">
            <Icon name="clock" className="w-4 h-4 text-slate-400" />
            Sessão de disputa
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Situação</div>
              <div className="text-xs text-slate-300">
                {proposal?.disputeSessionId ? 'Sessão criada no Hub' : 'Sem sessão no Hub'}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Abertura da sessão</div>
              <div className="text-xs text-slate-300">{item?.dataSessaoInicio ? fmtDateTime(item.dataSessaoInicio) : '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Modo de disputa</div>
              <div className="text-xs text-slate-300">{item?.modoDisputa || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Nosso total enviado</div>
              <div className="text-xs text-white font-medium">{fmt(ws.total)}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button
              onClick={proposal?.disputeSessionId ? actions.onOpenDispute : actions.onCreateDispute}
              className="px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-xs font-medium transition-colors"
            >
              {proposal?.disputeSessionId ? 'Abrir tela de Disputa' : 'Criar sessão e acompanhar'}
            </button>
            {temItensTipados && (
              <button
                onClick={actions.onAdequar}
                className="px-3 py-2 border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto rounded-lg text-xs font-medium transition-colors"
                title="Rateia o lance final entre os itens, respeitando os máximos do edital"
              >
                ⚡ Adequar ao lance final
              </button>
            )}
          </div>

          <div className="text-[10px] text-slate-500">
            Convocação para readequar a proposta chega nas mensagens do portal (painel abaixo) — o prazo costuma ser de 2 horas.
          </div>
        </div>
      )}

      {/* Resultado — vale nos dois modos (na disputa mostra o que já se sabe) */}
      <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-slate-200 font-medium">Resultado do certame</div>
          <div className="flex items-center gap-2">
            {item?.id && (
              <button
                onClick={async () => {
                  try {
                    await procurementService.syncCompetitors(item.id);
                    const r = await procurementService.getCompetitors(item.id);
                    setCompetidores(Array.isArray(r) ? r : r?.data || []);
                    actions.showToast('Concorrentes atualizados pelo PNCP', 'success');
                  } catch (e: any) {
                    actions.showToast('Sem resultados publicados no PNCP ainda', 'error');
                  }
                }}
                className="px-2.5 py-1 bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-200 rounded text-[11px] transition-colors"
              >
                Buscar resultado no PNCP
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Situação da licitação</div>
            {/* L5 (24/10/2026): o mapa das Licitações (andamento neutro, Suspensa âmbar, Revogada vermelha), o mesmo das
                outras telas, no lugar do badgeFor global, que pintava "participando" de azul. */}
            <span className={`inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded-full ${item?.status ? getStatusInfo(item.status).color : TONE_BADGE.neutral}`}>
              {item?.status ? getStatusInfo(item.status).label : '—'}
            </span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Valor homologado</div>
            <div className="text-xs text-slate-300">{homologado > 0 ? `R$ ${fmt(homologado)}` : '—'}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Nossa proposta</div>
            <div className="text-xs text-white font-medium">{fmt(ws.total)}</div>
          </div>
          {/* D-III: "quem levou" é pergunta diferente de "onde o processo está".
              A situação acima responde a segunda; esta célula responde a primeira. */}
          {item?.id && (
            <DesfechoPicker
              itemId={item.id}
              desfecho={item.desfecho}
              desfechoFonte={item.desfechoFonte}
              desfechoObs={item.desfechoObs}
              showToast={actions.showToast}
              abrirInicial={abrirDesfecho}
              onFechar={onDesfechoFechado}
              onSalvo={(atualizado) => {
                if (item) {
                  item.desfecho = atualizado?.desfecho;
                  item.desfechoFonte = atualizado?.desfechoFonte;
                  item.desfechoObs = atualizado?.desfechoObs;
                }
              }}
            />
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Concorrentes</div>
          {carregando && <div className="text-[11px] text-slate-500">Carregando…</div>}
          {!carregando && competidores?.length === 0 && (
            <div className="text-[11px] text-slate-500">Nenhum concorrente registrado — use "Buscar resultado no PNCP" após a homologação.</div>
          )}
          {!carregando && !!competidores?.length && (
            <div className="space-y-1">
              {competidores.map((c: any) => (
                <div key={c.id} className="flex items-center gap-3 bg-central-elevado rounded px-3 py-1.5">
                  <span className="text-[10px] text-slate-500 w-6">{c.rank ? `${c.rank}º` : '—'}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-300 truncate">{c.companyName}</div>
                    {c.companyCnpj && <div className="text-[10px] text-slate-500">{c.companyCnpj}</div>}
                  </div>
                  {c.proposalValue != null && (
                    <span className="text-xs text-slate-300">R$ {fmt(c.proposalValue)}</span>
                  )}
                  {c.isWinner && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${TONE_BADGE.success}`}>vencedor</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ⛔ Esta linha se chamava "Registrar desfecho" e tinha Ganhamos/Perdemos
            junto de Encerrada/Desistimos — os quatro chamando handleStatusChange,
            ou seja, escrevendo em `status`. Era ela que vinha corrompendo o dado:
            rótulo prometendo desfecho, gravação na coluna do PROCESSO, com os
            valores `ganho`/`perdido` que o enum marca como depreciados. Foi assim
            que o placar sumiu (D-III).
            Ganhamos/Perdemos migraram para o seletor de Desfecho na grade acima,
            que escreve na coluna própria. Aqui ficam só as duas que são MESMO
            ciclo de vida do processo — e o rótulo passa a dizer isso. */}
        <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-central-borda">
          <span className="text-[11px] text-slate-400 mr-1">Encerrar acompanhamento:</span>
          {[
            ['encerrada', '📋 Encerrada'],
            ['desistida', '🚪 Desistimos'],
          ].map(([st, label]) => (
            <button
              key={st}
              onClick={() => actions.onMarkResult(st)}
              className="px-2.5 py-1 bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-200 rounded text-[11px] transition-colors"
            >{label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
